import React, { useState, useEffect, useRef, useCallback } from "react";
import { useVideo } from "../../context/VideoContext";
import { safeGetItem, safeSetItem } from "../../utils/safeStorage";
import "./DelayBox.css";

const PIN_KEY = "delayBoxPinned";

const DelayBox = () => {
  const {
    subtitle1Delay,
    subtitle2Delay,
    setSubtitle1Delay,
    setSubtitle2Delay,
    showDelayBox,
    setShowDelayBox,
    subtitle1Loaded,
    subtitle2Loaded,
  } = useVideo();

  const [localDelay1, setLocalDelay1] = useState(subtitle1Delay);
  const [localDelay2, setLocalDelay2] = useState(subtitle2Delay);
  const [activeSub, setActiveSub] = useState(null); // برای تشخیص اینکه کدوم زیرنویس در حال تنظیمه
  const [isPinned, setIsPinned] = useState(
    () => safeGetItem(PIN_KEY, "false") === "true",
  );
  const timeoutRef = useRef(null);
  const boxRef = useRef(null);

  // همگام‌سازی با مقادیر global
  useEffect(() => {
    setLocalDelay1(subtitle1Delay);
    setLocalDelay2(subtitle2Delay);
  }, [subtitle1Delay, subtitle2Delay]);

  // مدیریت timeout برای بستن خودکار (وقتی پین شده، خودکار بسته نمی‌شود)
  useEffect(() => {
    if (showDelayBox) {
      // ریست تایمر قبلی
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // تایمر جدید (فقط وقتی پین نشده)
      if (!isPinned) {
        timeoutRef.current = setTimeout(() => {
          setShowDelayBox(false);
          setActiveSub(null);
        }, 3000);
      }
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [showDelayBox, localDelay1, localDelay2, isPinned, setShowDelayBox]);

  // تابع افزایش/کاهش تاخیر
  const adjustDelay = (subNumber, delta) => {
    // ریست تایمر (فقط وقتی پین نشده)
    if (!isPinned) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
          setShowDelayBox(false);
          setActiveSub(null);
        }, 3000);
      }
    }

    setActiveSub(subNumber);

    if (subNumber === 1) {
      const newDelay = Math.round((localDelay1 + delta) * 10) / 10; // یک رقم اعشار
      setLocalDelay1(newDelay);
      setSubtitle1Delay(newDelay);

      // اعمال تغییر روی زیرنویس
      applyDelayToSubtitle(1, newDelay);
    } else {
      const newDelay = Math.round((localDelay2 + delta) * 10) / 10;
      setLocalDelay2(newDelay);
      setSubtitle2Delay(newDelay);

      // اعمال تغییر روی زیرنویس
      applyDelayToSubtitle(2, newDelay);
    }
  };

  // اعمال تاخیر روی زیرنویس
  const applyDelayToSubtitle = (subNumber, delayMs) => {
    const video = document.querySelector("video");
    if (!video) return;

    const trackIndex = subNumber === 1 ? 0 : 1;
    const textTrack = video.textTracks[trackIndex];

    if (!textTrack || !textTrack.cues) return;

    // محاسبه تاخیر قبلی برای این زیرنویس
    const prevDelay = subNumber === 1 ? subtitle1Delay : subtitle2Delay;
    const deltaDelay = delayMs - prevDelay;

    if (deltaDelay === 0) return;

    // اعمال تغییر روی همه cueها
    Array.from(textTrack.cues).forEach((cue) => {
      cue.startTime += deltaDelay / 1000; // تبدیل به ثانیه
      cue.endTime += deltaDelay / 1000;
    });
  };

  // ریست تاخیر
  const resetDelay = (subNumber) => {
    const video = document.querySelector("video");
    if (!video) return;

    const trackIndex = subNumber === 1 ? 0 : 1;
    const textTrack = video.textTracks[trackIndex];

    if (!textTrack || !textTrack.cues) return;

    const currentDelay = subNumber === 1 ? localDelay1 : localDelay2;

    if (currentDelay !== 0) {
      // برگردوندن همه cueها به حالت اولیه
      Array.from(textTrack.cues).forEach((cue) => {
        cue.startTime -= currentDelay / 1000;
        cue.endTime -= currentDelay / 1000;
      });
    }

    if (subNumber === 1) {
      setLocalDelay1(0);
      setSubtitle1Delay(0);
    } else {
      setLocalDelay2(0);
      setSubtitle2Delay(0);
    }
  };

  // ریست هر دو زیرنویس
  const resetBoth = () => {
    if (subtitle1Loaded) resetDelay(1);
    if (subtitle2Loaded) resetDelay(2);
  };

  const handleBoxClick = useCallback((e) => {
    e.stopPropagation(); // این جلوی انتشار رویداد به والد رو می‌گیره
  }, []);

  // پین کردن / باز کردن (ذخیره در localStorage)
  const handleTogglePin = () => {
    const next = !isPinned;
    setIsPinned(next);
    safeSetItem(PIN_KEY, next ? "true" : "false");
  };

  // بستن باکس
  const handleClose = () => {
    setShowDelayBox(false);
    setActiveSub(null);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  // رندر تک‌سطرِ تاخیر برای هر زیرنویس (برچسب - دکمه‌ها - مقدار)
  const renderDelayRow = (subNumber) => {
    const isSub1 = subNumber === 1;
    const loaded = isSub1 ? subtitle1Loaded : subtitle2Loaded;
    if (!loaded) return null;

    const localDelay = isSub1 ? localDelay1 : localDelay2;

    return (
      <div
        className={`delay-item ${activeSub === subNumber ? "active" : ""}`}
      >
        <span className={`delay-subtitle-badge ${isSub1 ? "sub1" : "sub2"}`}>
          {isSub1 ? "زیرنویس ۱" : "زیرنویس ۲"}
        </span>
        <div className="delay-controls">
          <button
            className="delay-btn"
            onMouseDown={() => adjustDelay(subNumber, -100)}
            onTouchStart={() => adjustDelay(subNumber, -100)}
          >
            <i className="fas fa-minus"></i> -۱۰۰
          </button>
          <button
            className="delay-btn"
            onMouseDown={() => adjustDelay(subNumber, -10)}
            onTouchStart={() => adjustDelay(subNumber, -10)}
          >
            -۱۰
          </button>
          <button
            className="delay-btn reset-btn"
            onClick={() => resetDelay(subNumber)}
          >
            <i className="fas fa-undo"></i>
          </button>
          <button
            className="delay-btn"
            onMouseDown={() => adjustDelay(subNumber, 10)}
            onTouchStart={() => adjustDelay(subNumber, 10)}
          >
            +۱۰
          </button>
          <button
            className="delay-btn"
            onMouseDown={() => adjustDelay(subNumber, 100)}
            onTouchStart={() => adjustDelay(subNumber, 100)}
          >
            <i className="fas fa-plus"></i> +۱۰۰
          </button>
        </div>
        <span className="delay-value">{localDelay} ms</span>
      </div>
    );
  };

  if (!showDelayBox) return null;

  return (
    <div
      className={`delay-box${isPinned ? " pinned" : ""}`}
      onClick={handleBoxClick}
      ref={boxRef}
    >
      <div className="delay-box-header">
        <button
          className={`delay-box-pin${isPinned ? " pinned" : ""}`}
          onClick={handleTogglePin}
          title={
            isPinned
              ? "بستن خودکار غیرفعال است (برای بستن، ضربدر را بزنید)"
              : "پین کردن (غیرفعال‌سازی بستن خودکار)"
          }
          aria-pressed={isPinned}
        >
          <i className="fas fa-thumbtack"></i>
        </button>
        <span>تنظیم تاخیر زیرنویس (میلی‌ثانیه)</span>
        <button className="delay-box-close" onClick={handleClose}>
          <i className="fas fa-times"></i>
        </button>
      </div>

      <div className="delay-box-content">
        {renderDelayRow(1)}
        {renderDelayRow(2)}

        {/* دکمه ریست کلی */}
        {/* {(subtitle1Loaded || subtitle2Loaded) && (
          <div className="delay-reset-all">
            <button className="delay-btn reset-all-btn" onClick={resetBoth}>
              <i className="fas fa-undo-alt"></i> ریست هر دو
            </button>
          </div>
        )} */}
      </div>
    </div>
  );
};

export default DelayBox;
